/**
 * What a decision table's CSV says about where its rows came from, on every row
 * (bata-ui#166 W4-8, `docs/adr/9120`).
 *
 * ADR 9010 made every export a single CSV. That retired the workbook, and with it the one
 * place the nine provenance fields were written: `provenanceRows` renders them into an XLSX
 * sheet that is never produced now. The in-season decision tables therefore left the product
 * with no run, no metric definitions and no platform version on them. A proposal file pasted
 * into a mail could not be traced back to the cycle that produced it.
 *
 * So the provenance is written into the rows, as columns. It is the same on every row of one
 * file, which is repetition, but a CSV has no header block that every parser keeps. A row
 * copied out of the file still names its run, its tick and the metric definitions it was
 * computed under.
 *
 * **Where each value comes from, and what happens when it cannot be read.**
 *
 *   run            `GET /inseason/runs` (the run directory the picker reads), matched by id.
 *                  The run's own `metric_definition_version`, `cycle_tick`, plan version or
 *                  its absent reason, parameter set, input batch, forecast run and commit.
 *   platform       `GET /version`: the running platform release and its commit. It does NOT
 *                  publish a metric definition version (G7.5 fields only), so the file cites
 *                  the RUN's version, which is also the correct one for a run's rows.
 *   interface      this build (`buildVersion`).
 *
 * **A simulation's file** (`docs/adr/6027`) is the same builder over `ExportContext.simulation`: it
 * names the simulation where a run's names the run, and states as absent what the platform does not
 * expose for a scenario's run — it never reads those from the base run.
 *
 * Nothing here throws. A read that fails leaves its columns `not-reported`, and the
 * `provenance_absent` column names what could not be stated and why. A resolver that threw
 * would stop the whole download over a missing citation, and a file with one column saying
 * why it is incomplete is better than no file.
 */

import { callOperation, collectPages } from '@/api';
import { inseasonListRuns, version as platformVersionOperation } from '@/api/generated';
import type { CycleRunSummary, SimulationView, VersionResponse } from '@/api/generated';
import { NOT_PUBLISHED, buildVersion } from '@/export';
import type { ExportCell, ExportColumn, ExportRequest, MeasureCitation, Provenance, ProvenanceFilter } from '@/export';

/** Why the run's own provenance is not on the rows. Codes, written verbatim into the file. */
export type RunAbsence = 'run_unset' | 'run_not_listed' | 'run_directory_unread';

/** Everything the provenance columns are written from, read once per download. */
export interface ExportContext {
  readonly runId: string | null;
  /** The run as the directory lists it, or `null` with `runAbsence` saying why. */
  readonly run: CycleRunSummary | null;
  readonly runAbsence: RunAbsence | null;
  /** `GET /version`, or `null` when it could not be read. */
  readonly platform: VersionResponse | null;
  /** UTC, ISO-8601. The same instant on every row and in the provenance object. */
  readonly generatedAt: string;
  /**
   * Set only when the file's lines are a SIMULATION's (`docs/adr/6027`). The file then names the
   * simulation where a live file names its run, and states as absent every field the platform
   * does not expose for a simulation — it never reads them from the base run, which may differ.
   * `run` and `runAbsence` are not consulted for such a file.
   */
  readonly simulation?: SimulationView;
}

/** Resolves an ICU key. Handed in, so the sheet builders stay pure and testable. */
export type Translate = (key: string, values?: Readonly<Record<string, unknown>>) => string;

/**
 * Reads the run and the platform version. Never rejects: each half that fails is recorded as
 * absent. The directory is walked whole, as the run picker walks it, because it takes no
 * `run_id` filter. W4-5's per-run provenance read replaces this walk when it is published.
 */
export async function readExportContext(runId: string | null, now: () => Date = () => new Date()): Promise<ExportContext> {
  const [runs, platform] = await Promise.allSettled([
    runId === null ? Promise.resolve(null) : collectPages(inseasonListRuns, { query: {} }),
    callOperation(platformVersionOperation, {}),
  ]);
  let run: CycleRunSummary | null = null;
  let runAbsence: RunAbsence | null = null;
  if (runId === null) runAbsence = 'run_unset';
  else if (runs.status === 'rejected' || runs.value === null) runAbsence = 'run_directory_unread';
  else {
    run = runs.value.find((entry) => entry.run_id === runId) ?? null;
    if (run === null) runAbsence = 'run_not_listed';
  }
  return {
    runId,
    run,
    runAbsence,
    platform: platform.status === 'fulfilled' ? platform.value : null,
    generatedAt: now().toISOString(),
  };
}

// ── measure citations ────────────────────────────────────────────────────────

/**
 * A measure an export cites. `registeredIn` is the `metrics-vN` that introduced it in the
 * bata-data register (`semantic/metrics/<name>.yml`, `introduced_in`). Leave it out for a
 * measure the register does not define: the file then says `not_registered`, which is the
 * truth, rather than borrowing a version for a definition that was never registered.
 */
export interface CitedMeasure {
  readonly measure: string;
  readonly definition: string;
  readonly registeredIn?: string;
}

function versionNumber(version: string): number | null {
  const match = /^metrics-v(\d+)$/.exec(version.trim());
  return match === null ? null : Number(match[1]);
}

/** The citation's `registerVersion` code under the run's declared metric definitions. */
export function registerVersionFor(measure: CitedMeasure, context: ExportContext): string {
  if (measure.registeredIn === undefined) return 'not_registered';
  // A simulation does not expose the metric-definition version its run was computed under, and the
  // base run's is not the scenario's to borrow: the citation says so rather than claiming a version.
  if (context.simulation !== undefined) return SIMULATION_METRICS_NOT_EXPOSED;
  if (context.run === null) return 'version_not_read';
  const declared = context.run.metric_definition_version;
  const declaredNumber = versionNumber(declared);
  const introduced = versionNumber(measure.registeredIn);
  if (declaredNumber === null || introduced === null) return 'version_not_declared';
  // A measure introduced after the version the run declares is not defined under it. Citing
  // the run's version would claim a definition that did not exist when the run was computed.
  return introduced <= declaredNumber ? declared : 'not_registered_at_run_version';
}

export function citeMeasures(measures: readonly CitedMeasure[], context: ExportContext): readonly MeasureCitation[] {
  return measures.map((measure) => ({
    measure: measure.measure,
    definition: measure.definition,
    registerVersion: registerVersionFor(measure, context),
  }));
}

// ── the provenance columns ───────────────────────────────────────────────────

/**
 * Header keys spelled out one by one so the catalogue check sees every key. `provenance: true`: the
 * CSV carries them on every row; the Excel workbook writes them once, in its «Tecnico» sheet
 * (DEC-104, `docs/adr/9041`).
 */
export const PROVENANCE_COLUMNS: readonly ExportColumn[] = [
  { key: 'run_id', headerKey: 'inseason.exportProvenance.column.runId', kind: 'text', provenance: true, descriptionKey: 'inseason.columnGuide.provenance.run_id' },
  { key: 'cycle_tick', headerKey: 'inseason.exportProvenance.column.cycleTick', kind: 'text', provenance: true, descriptionKey: 'inseason.columnGuide.provenance.cycle_tick' },
  { key: 'run_recorded_at', headerKey: 'inseason.exportProvenance.column.runRecordedAt', kind: 'date', provenance: true, descriptionKey: 'inseason.columnGuide.provenance.run_recorded_at' },
  { key: 'metric_definition_version', headerKey: 'inseason.exportProvenance.column.metricDefinitionVersion', kind: 'text', provenance: true, descriptionKey: 'inseason.columnGuide.provenance.metric_definition_version' },
  { key: 'measure_definitions', headerKey: 'inseason.exportProvenance.column.measureDefinitions', kind: 'text', provenance: true, descriptionKey: 'inseason.columnGuide.provenance.measure_definitions' },
  { key: 'plan_version_id', headerKey: 'inseason.exportProvenance.column.planVersionId', kind: 'text', provenance: true, descriptionKey: 'inseason.columnGuide.provenance.plan_version_id' },
  { key: 'plan_version_absent_reason', headerKey: 'inseason.exportProvenance.column.planVersionAbsentReason', kind: 'label', provenance: true, descriptionKey: 'inseason.columnGuide.provenance.plan_version_absent_reason' },
  { key: 'parameter_set_id', headerKey: 'inseason.exportProvenance.column.parameterSetId', kind: 'text', provenance: true, descriptionKey: 'inseason.columnGuide.provenance.parameter_set_id' },
  { key: 'batch_id', headerKey: 'inseason.exportProvenance.column.batchId', kind: 'text', provenance: true, descriptionKey: 'inseason.columnGuide.provenance.batch_id' },
  { key: 'forecast_run_id', headerKey: 'inseason.exportProvenance.column.forecastRunId', kind: 'text', provenance: true, descriptionKey: 'inseason.columnGuide.provenance.forecast_run_id' },
  { key: 'run_commit_sha', headerKey: 'inseason.exportProvenance.column.runCommitSha', kind: 'text', provenance: true, descriptionKey: 'inseason.columnGuide.provenance.run_commit_sha' },
  { key: 'platform_version', headerKey: 'inseason.exportProvenance.column.platformVersion', kind: 'text', provenance: true, descriptionKey: 'inseason.columnGuide.provenance.platform_version' },
  { key: 'ui_version', headerKey: 'inseason.exportProvenance.column.uiVersion', kind: 'text', provenance: true, descriptionKey: 'inseason.columnGuide.provenance.ui_version' },
  { key: 'generated_at', headerKey: 'inseason.exportProvenance.column.generatedAt', kind: 'date', provenance: true, descriptionKey: 'inseason.columnGuide.provenance.generated_at' },
  { key: 'provenance_absent', headerKey: 'inseason.exportProvenance.column.absent', kind: 'text', provenance: true, descriptionKey: 'inseason.columnGuide.provenance.provenance_absent' },
];

const NOT_REPORTED: ExportCell = { kind: 'missing', state: 'not-reported' };
/** A field that does not apply to this row, e.g. the absent reason of a plan version that is present. */
const NOT_APPLICABLE: ExportCell = { kind: 'missing', state: 'not-ranged' };

function textOr(value: string | null | undefined): ExportCell {
  return value === null || value === undefined || value.trim() === '' ? NOT_REPORTED : { kind: 'text', value };
}

/** The platform release as `/version` states it: `0.52.0 (commit)`. */
function platformVersionText(platform: VersionResponse): string {
  return [platform.version, `(${platform.commitSha.slice(0, 12)})`].join(' ');
}

// ── a simulation's file (docs/adr/6027) ──────────────────────────────────────

/** The register version a simulation's file cites for a registered measure: none is exposed for it. */
export const SIMULATION_METRICS_NOT_EXPOSED = 'metric_version_not_exposed_by_simulation';

/**
 * The fields `GET /inseason/simulations/{id}` does not carry for the scenario's run — they live on
 * the scenario's own run row, which no live read may show (bata-platform ADR 6181). Each is written
 * `not-reported` on the rows and listed in `provenance_absent` as `<field>_not_exposed_by_simulation`.
 * They are NEVER copied from the base run: a simulation may run under another forecast, commit,
 * objective or metric-definition version than the run it branched from.
 */
export const SIMULATION_UNEXPOSED_FIELDS = [
  'cycle_tick', 'run_recorded_at', 'metric_definition_version', 'plan_version', 'batch_id',
  'forecast_run_id', 'run_commit_sha', 'objective_config_id',
] as const;

/** The code a simulation's file gives for a disposition that is empty by construction. */
export const SIMULATION_NOT_DECIDABLE = 'disposition_scenario_not_decidable';
/** The plan version's absent-reason code on a simulation's rows. */
export const SIMULATION_PLAN_VERSION_REASON = 'not_exposed_by_simulation';

/**
 * The context of a simulation's file: the simulation as the platform states it, and the platform
 * version. Never rejects — an unread `/version` is recorded as absent, as for a run.
 */
export async function readSimulationExportContext(
  simulation: SimulationView, now: () => Date = () => new Date(),
): Promise<ExportContext> {
  const [platform] = await Promise.allSettled([callOperation(platformVersionOperation, {})]);
  return {
    runId: simulation.simulation_id,
    run: null,
    runAbsence: null,
    platform: platform.status === 'fulfilled' ? platform.value : null,
    generatedAt: now().toISOString(),
    simulation,
  };
}

/**
 * What names a simulation's file, as the provenance object's filters: the simulation, the run it
 * branched from, the scenario it wrote into, its parameter set, the registry it was evaluated
 * against and the overlay (the parameter keys it set). With these the file's numbers re-derive from
 * what it names (G9).
 */
export function simulationFilters(simulation: SimulationView): readonly ProvenanceFilter[] {
  const equals = (field: string, value: string): ProvenanceFilter => ({ field, operator: '=', value });
  return [
    equals('simulation_id', simulation.simulation_id),
    equals('base_run_id', simulation.base_run_id),
    equals('stage', simulation.stage),
    equals('scenario_id', simulation.scenario_id),
    equals('parameter_set_id', simulation.parameter_set_id),
    equals('registry_fingerprint', simulation.registry_fingerprint),
    equals('overlay_digest', simulation.overlay_digest),
    equals('requested_at', simulation.requested_at),
    ...Object.entries(simulation.overlay)
      .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
      .map(([key, value]): ProvenanceFilter => ({ field: 'overlay', operator: 'sets', value: `${key}=${value}` })),
  ];
}

function simulationProvenanceCells(
  context: ExportContext, simulation: SimulationView, citations: readonly MeasureCitation[], notes: readonly string[],
): readonly ExportCell[] {
  const absent: string[] = SIMULATION_UNEXPOSED_FIELDS.map((field) => `${field}_not_exposed_by_simulation`);
  if (context.platform === null) absent.push('platform_version_unread');
  // The file says once more what no row can: its dispositions are empty because a scenario is never decided.
  absent.push(SIMULATION_NOT_DECIDABLE);
  absent.push(...notes);
  return [
    { kind: 'text', value: simulation.simulation_id },
    NOT_REPORTED,
    NOT_REPORTED,
    NOT_REPORTED,
    { kind: 'text', value: citations.map((citation) => [citation.measure, citation.registerVersion].join('@')).join(';') },
    NOT_REPORTED,
    { kind: 'label', value: SIMULATION_PLAN_VERSION_REASON, code: SIMULATION_PLAN_VERSION_REASON },
    textOr(simulation.parameter_set_id),
    NOT_REPORTED,
    NOT_REPORTED,
    NOT_REPORTED,
    context.platform === null ? NOT_REPORTED : { kind: 'text', value: platformVersionText(context.platform) },
    { kind: 'text', value: buildVersion() },
    { kind: 'date', iso: context.generatedAt },
    { kind: 'text', value: absent.join(';') },
  ];
}

/**
 * The cells every row of the file carries, in `PROVENANCE_COLUMNS` order.
 *
 * `provenance_absent` lists what the file could not state, as codes joined by `;`, or `none`.
 * It is never blank, because a blank would not say whether everything was stated or nothing
 * was checked.
 *
 * `notes` are what the file left out ON PURPOSE and says so, as codes (`name=count`): the dropped
 * doors' lines, which sit on their own sheet of the workbook and out of the CSV
 * (`./dropped-door-export.ts`). They follow what could not be read, so a reader scanning for a
 * missing citation meets those first.
 */
export function provenanceCells(
  context: ExportContext, citations: readonly MeasureCitation[], notes: readonly string[] = [],
): readonly ExportCell[] {
  if (context.simulation !== undefined) return simulationProvenanceCells(context, context.simulation, citations, notes);
  const { run } = context;
  const absent: string[] = [];
  if (context.runAbsence !== null) absent.push(context.runAbsence);
  if (run !== null && (run.cycle_tick === null || run.cycle_tick === '')) absent.push('cycle_tick_not_recorded');
  if (run !== null && run.metric_definition_version.trim() === '') absent.push('metric_definition_version_not_declared');
  if (context.platform === null) absent.push('platform_version_unread');
  absent.push(...notes);

  const planVersion = run?.plan_version_id ?? null;
  const planAbsentReason = run?.plan_version_absent_reason ?? null;
  const planVersionCell = run === null ? NOT_REPORTED : textOr(planVersion);
  const planReasonCell: ExportCell = run === null
    ? NOT_REPORTED
    : planVersion !== null && planVersion !== ''
      ? NOT_APPLICABLE
      : planAbsentReason === null || planAbsentReason === ''
        ? NOT_REPORTED
        : { kind: 'label', value: planAbsentReason, code: planAbsentReason };
  if (run !== null && (planVersion === null || planVersion === '') && (planAbsentReason === null || planAbsentReason === '')) {
    absent.push('plan_version_not_published');
  }

  return [
    context.runId === null ? NOT_REPORTED : { kind: 'text', value: context.runId },
    run === null ? NOT_REPORTED : textOr(run.cycle_tick),
    run === null || run.recorded_at === '' ? NOT_REPORTED : { kind: 'date', iso: run.recorded_at },
    run === null ? NOT_REPORTED : textOr(run.metric_definition_version),
    { kind: 'text', value: citations.map((citation) => [citation.measure, citation.registerVersion].join('@')).join(';') },
    planVersionCell,
    planReasonCell,
    run === null ? NOT_REPORTED : textOr(run.parameter_set_id),
    run === null ? NOT_REPORTED : textOr(run.batch_id),
    run === null ? NOT_REPORTED : textOr(run.forecast_run_id),
    run === null ? NOT_REPORTED : textOr(run.commit_sha),
    context.platform === null ? NOT_REPORTED : { kind: 'text', value: platformVersionText(context.platform) },
    { kind: 'text', value: buildVersion() },
    { kind: 'date', iso: context.generatedAt },
    { kind: 'text', value: absent.length === 0 ? 'none' : absent.join(';') },
  ];
}

/**
 * The provenance object, filled from the run where the run states it. The object is what the
 * shared service reads (the file name takes `hierarchyNode`); the columns are what the reader
 * of the CSV sees. Both are written from the same context so they cannot disagree.
 */
export function runProvenance(base: Provenance, context: ExportContext, citations: readonly MeasureCitation[]): Provenance {
  const platformText = context.platform === null
    ? base.platformVersion
    : [buildVersion(), `bata-platform ${platformVersionText(context.platform)}`].join(' · ');
  if (context.simulation !== undefined) {
    const { simulation } = context;
    // The simulation's own perimeter replaces the run filter: it is the simulation the numbers belong to.
    return {
      ...base,
      filters: simulationFilters(simulation),
      hierarchyNode: simulation.country_key,
      timeWindow: { from: simulation.window_start, to: simulation.window_end },
      scenario: simulation.scenario_id,
      // The plan version of a scenario's run is not exposed: stated absent, never the base run's.
      planVersionId: NOT_PUBLISHED,
      measureDefinitions: citations,
      generatedAt: context.generatedAt,
      platformVersion: platformText,
    };
  }
  const { run } = context;
  const planVersion = run?.plan_version_id ?? null;
  return {
    ...base,
    hierarchyNode: run === null ? base.hierarchyNode : run.country_key,
    planVersionId: planVersion === null || planVersion === '' ? base.planVersionId : planVersion,
    measureDefinitions: citations,
    generatedAt: context.generatedAt,
    platformVersion: platformText,
  };
}

/**
 * The request with the provenance columns appended to every row and to the perimeter's
 * visible columns. Only for a request whose rows are an array (every builder in this chapter
 * builds one); the rows are materialised once.
 */
export function withRunProvenance(
  request: ExportRequest, context: ExportContext, measures: readonly CitedMeasure[], notes: readonly string[] = [],
): ExportRequest {
  const citations = citeMeasures(measures, context);
  const cells = provenanceCells(context, citations, notes);
  const rows = [...(request.rows() as Iterable<readonly ExportCell[]>)].map((row) => [...row, ...cells]);
  return {
    ...request,
    columns: [...request.columns, ...PROVENANCE_COLUMNS],
    rows: () => rows,
    rowCount: rows.length,
    provenance: runProvenance(request.provenance, context, citations),
    perimeter: {
      ...request.perimeter,
      visibleColumns: [...request.perimeter.visibleColumns, ...PROVENANCE_COLUMNS.map((column) => column.key)],
    },
  };
}
